/**
 * Green slope vector lookup.
 *
 * Vectors are stored in Firestore as normalized (0–1) image coordinates.
 * Image convention:
 *   x=0 left edge, x=1 right edge (when facing the green from the fairway)
 *   y=0 top edge (back of green), y=1 bottom edge (front of green / approach side)
 *
 * Ball and pin positions are converted from GPS lat/lng offsets (ft) from
 * green center into the same normalized image space using the hole azimuth
 * to rotate world-frame into image-frame.
 */

// The tap-crop image shown to the user (vectorTapImageUrl) is rendered with
// extra margin beyond the green's own greenHalfWidthFt/greenHalfDepthFt --
// a crop tight to just the green boundary left no room for approach shots
// landing in the fringe/apron just off the putting surface, which is
// common and was reported as the image looking "cropped"/"over magnified"
// (see tools/vectorizer/regenerate_tap_crops_corrected.py). The margin is
// NOT a fixed ratio: it's capped per hole, per axis, to whatever real page
// content is actually available around the green (so the crop never has to
// synthesize/smear pixels, which looked like colored streaking on holes
// with little room, e.g. hole 5). So the actual ratio is recovered per hole
// from Firestore's tapImageHalfWidthFt/tapImageHalfDepthFt vs.
// greenHalfWidthFt/greenHalfDepthFt -- see getTapImageMarginRatios below.
// xNorm/yNorm (0-1) everywhere else in this file, and everywhere ball/pin
// positions are stored, ALWAYS refer to the green's own true
// half-dimensions -- never the padded image. These helpers are the only
// place that converts between that frame and the wider image's own 0-1
// fraction, used solely for tap handling and marker rendering in
// ShotPlanner.tsx/PinSheetSetup.tsx.
export type TapImageMarginRatios = { x: number; y: number };

// Derives the per-axis margin ratio (padded tap-image half-dimension /
// true green half-dimension) from a hole's Firestore fields. Falls back to
// 1 (no margin, i.e. the image is exactly the green's own bounds) when the
// new fields are absent, matching pre-margin behavior for any hole/course
// not yet regenerated with margin.
export function getTapImageMarginRatios(hole: {
  tapImageHalfWidthFt?: number;
  tapImageHalfDepthFt?: number;
  greenHalfWidthFt?: number;
  greenHalfDepthFt?: number;
}): TapImageMarginRatios {
  const x =
    hole.tapImageHalfWidthFt && hole.greenHalfWidthFt
      ? hole.tapImageHalfWidthFt / hole.greenHalfWidthFt
      : 1;
  const y =
    hole.tapImageHalfDepthFt && hole.greenHalfDepthFt
      ? hole.tapImageHalfDepthFt / hole.greenHalfDepthFt
      : 1;
  return { x, y };
}

// Image tap fraction (0-1 across the padded, displayed image) -> true
// xNorm/yNorm (0-1 across the green's real half-dimensions, per the
// convention above). Not clamped here -- callers clamp to [0,1] after,
// same as they already did for the un-padded image, so a tap in the
// margin area just registers at the green's nearest edge.
export function imageFracToXNorm(frac: number, ratio: number): number {
  return 0.5 + (frac - 0.5) * ratio;
}

// Inverse: true xNorm/yNorm -> image tap fraction, for positioning a
// marker on the padded, displayed image.
export function xNormToImageFrac(xNorm: number, ratio: number): number {
  return 0.5 + (xNorm - 0.5) / ratio;
}

export type GreenVector = {
  x: number;              // normalized 0–1 left-right in image
  y: number;              // normalized 0–1 top-bottom in image
  slopePct: number;
  slopeDeg: number;
  directionDeg: number | null;
  // "heatmap_arrow": read directly off the green-book slope panel — the
  // arrow's own printed angle for direction, the underlying heat-map color
  // for magnitude. "ocr_gradient": the older method, direction inferred
  // from the spacing between nearby printed slope numbers (see
  // vectorize_wh.py) rather than a real arrow — kept for data already
  // uploaded under it, but heatmap_arrow supersedes it going forward.
  source: "ocr_only" | "arrow_only" | "combined" | "ocr_gradient" | "heatmap_arrow";
  color?: string;
};

export type GreenVectorDoc = {
  course: string;
  hole: number;
  imageWidthPx: number;
  imageHeightPx: number;
  splitYPx?: number;
  vectors: GreenVector[];
};

export type PuttSlopeProfile = {
  thetaStartDeg: number;
  thetaMidDeg: number;
  thetaApexDeg: number;
  dominantDirectionDeg: number | null;
  vectorsUsed: number;
};

// ---------------------------------------------------------------------------
// GPS → image-normalized coordinate conversion
// ---------------------------------------------------------------------------

/**
 * Convert a GPS offset from green center (ftNorth, ftEast in world frame)
 * into normalized image coords (0–1) using the hole approach azimuth.
 *
 * holeAzimuthDeg: compass bearing from tee toward green (approach direction).
 * In the image, y=1 is the front of the green (approach side), y=0 is the back.
 * x=0 is the left edge, x=1 is the right edge when facing the green.
 *
 * greenHalfWidthFt / greenHalfDepthFt: half-dimensions of the green in ft.
 * Default 45ft (≈15yd) each — tunable per hole.
 */
export function gpsOffsetToImageNorm(params: {
  ftNorth: number;
  ftEast: number;
  holeAzimuthDeg: number;
  greenHalfWidthFt?: number;
  greenHalfDepthFt?: number;
}): { xNorm: number; yNorm: number } {
  const {
    ftNorth,
    ftEast,
    holeAzimuthDeg,
    greenHalfWidthFt = 45,
    greenHalfDepthFt = 45,
  } = params;

  // Rotate world-frame (N, E) into approach-frame (forward = away from approach)
  // The approach direction is holeAzimuthDeg (compass bearing toward the green).
  // "Forward" in approach frame = INTO the green = AWAY from the player.
  // Image y=0 = back of green = most "forward" from approach side.

  const azRad = (holeAzimuthDeg * Math.PI) / 180;

  // Project onto approach axis (positive = deeper into green from fairway)
  const ftForward = ftNorth * Math.cos(azRad) + ftEast * Math.sin(azRad);
  // Project onto right axis (positive = right when facing green)
  const ftRight = -ftNorth * Math.sin(azRad) + ftEast * Math.cos(azRad);

  // Map: forward → y (0 = back, 1 = front/approach side)
  // y=1 is approach side (ftForward = most negative = closest to player)
  const yNorm = 0.5 - ftForward / (2 * greenHalfDepthFt);
  const xNorm = 0.5 + ftRight / (2 * greenHalfWidthFt);

  return {
    xNorm: Math.max(0, Math.min(1, xNorm)),
    yNorm: Math.max(0, Math.min(1, yNorm)),
  };
}

// ---------------------------------------------------------------------------
// Vector sampling
// ---------------------------------------------------------------------------

function dist2(ax: number, ay: number, bx: number, by: number) {
  return (ax - bx) ** 2 + (ay - by) ** 2;
}

function weightedAvgSlopeDeg(
  vectors: GreenVector[],
  refX: number,
  refY: number,
  topN: number,
): number {
  if (!vectors.length) return 0;

  const sorted = [...vectors].sort(
    (a, b) => dist2(a.x, a.y, refX, refY) - dist2(b.x, b.y, refX, refY),
  );

  const nearest = sorted.slice(0, topN);

  let weightSum = 0;
  let slopeSum = 0;

  for (const v of nearest) {
    const d = Math.sqrt(dist2(v.x, v.y, refX, refY)) + 0.001;
    const w = 1 / (d * d);
    slopeSum += v.slopeDeg * w;
    weightSum += w;
  }

  return weightSum > 0 ? slopeSum / weightSum : 0;
}

/**
 * Find the dominant slope direction from vectors near a point.
 * Returns the average directionDeg of the top N nearest vectors that have one.
 */
function dominantDirection(
  vectors: GreenVector[],
  refX: number,
  refY: number,
  topN: number,
): number | null {
  const withDir = vectors.filter((v) => v.directionDeg != null);
  if (!withDir.length) return null;

  const sorted = [...withDir].sort(
    (a, b) => dist2(a.x, a.y, refX, refY) - dist2(b.x, b.y, refX, refY),
  );

  const nearest = sorted.slice(0, topN);

  // Circular mean of direction angles
  let sinSum = 0;
  let cosSum = 0;
  for (const v of nearest) {
    const rad = (v.directionDeg! * Math.PI) / 180;
    sinSum += Math.sin(rad);
    cosSum += Math.cos(rad);
  }

  const meanRad = Math.atan2(sinSum / nearest.length, cosSum / nearest.length);
  return ((meanRad * 180) / Math.PI + 360) % 360;
}

/**
 * One point along the ball→pin line, carrying its own local slope reading
 * rather than being blended into a single green-wide number. This is the
 * "read the arrows along the line, then the heat map for where it's
 * steepest" approach: each sample keeps its own magnitude (slopeDeg) and
 * direction, so a green with more than one real fall line shows up as an
 * actual reversal between samples instead of being averaged away.
 */
export type PuttPathSample = {
  t: number; // 0 (ball) .. 1 (pin)
  x: number;
  y: number;
  slopeDeg: number;
  directionDeg: number | null;
};

/**
 * Bearing of the ball→pin line, in the same image-frame convention as
 * GreenVector.directionDeg (0 = toward the back of the green, 90 = right,
 * 180 = toward the front/approach side, 270 = left).
 */
export function puttLineBearingDeg(
  ballXNorm: number,
  ballYNorm: number,
  pinXNorm: number,
  pinYNorm: number,
): number {
  const dx = pinXNorm - ballXNorm;
  const dy = pinYNorm - ballYNorm;
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return 0;
  return ((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360;
}

function weightedDirectionDeg(
  vectors: GreenVector[],
  refX: number,
  refY: number,
  topN: number,
): number | null {
  const withDir = vectors.filter((v) => v.directionDeg != null);
  if (!withDir.length) return null;

  const sorted = [...withDir].sort(
    (a, b) => dist2(a.x, a.y, refX, refY) - dist2(b.x, b.y, refX, refY),
  );
  const nearest = sorted.slice(0, topN);

  let sinSum = 0;
  let cosSum = 0;
  let weightSum = 0;
  for (const v of nearest) {
    const d = Math.sqrt(dist2(v.x, v.y, refX, refY)) + 0.001;
    const w = 1 / (d * d);
    const rad = (v.directionDeg! * Math.PI) / 180;
    sinSum += Math.sin(rad) * w;
    cosSum += Math.cos(rad) * w;
    weightSum += w;
  }
  if (weightSum <= 0) return null;

  return ((Math.atan2(sinSum, cosSum) * 180) / Math.PI + 360) % 360;
}

/**
 * Local weighted-least-squares plane fit of the slope field at a point,
 * replacing weightedAvgSlopeDeg/weightedDirectionDeg's plain nearest-
 * neighbor blend for computePuttBreakFromPath's read (sampleVectorsForPutt,
 * the deprecated fallback, still uses the simple version).
 *
 * Each vector's (slopeDeg, directionDeg) is really a sample of an
 * underlying 2D downhill-gradient field: gx = slopeDeg*sin(dirRad),
 * gy = -slopeDeg*cos(dirRad) (same 0=up/90=right convention as
 * directionDeg elsewhere). Fitting a local linear model of that field,
 * g(dx,dy) ~= a0 + a1*dx + a2*dy in coordinates centered on the query
 * point, and evaluating it there (dx=dy=0, so the answer is just the
 * fitted intercept a0/b0) extrapolates the surrounding slope TREND into
 * sparse areas instead of nearest-neighbor blending, which can be
 * misleading when real coverage is thin.
 *
 * This matters specifically near a green's boundary: arrow detection is
 * intentionally eroded ~15px in from the edge (see heatmap_arrow_core.py)
 * so glyphs don't fuse with the boundary line, which means genuine arrow
 * coverage thins out right where a front/back pin often sits. Confirmed
 * on Brentwood hole 16 (a front pin, real on-course "don't agree" read):
 * the nearest real arrow to the pin was 0.13-0.20 normalized units away
 * (vs. ~0.08 near mid-green), pulling the old nearest-neighbor blend from
 * a cluster whose direction differed by ~90 degrees from the rest of the
 * putt line, versus reasonably continuing that line's own trend.
 *
 * Falls back to the plain weighted average (equivalent to dropping the
 * a1/a2 terms) when there are too few neighbors or they're too close to
 * collinear for a stable 3-parameter fit.
 */
function weightedVectorFieldAt(
  vectors: GreenVector[],
  refX: number,
  refY: number,
  topN: number,
): { slopeDeg: number; directionDeg: number | null } {
  const withDir = vectors.filter((v) => v.directionDeg != null);
  if (!withDir.length) return { slopeDeg: 0, directionDeg: null };

  const sorted = [...withDir].sort(
    (a, b) => dist2(a.x, a.y, refX, refY) - dist2(b.x, b.y, refX, refY),
  );
  const nearest = sorted.slice(0, Math.max(topN, 3));

  const pts = nearest.map((v) => {
    const dx = v.x - refX;
    const dy = v.y - refY;
    const d = Math.sqrt(dx * dx + dy * dy) + 0.001;
    const w = 1 / (d * d);
    const rad = (v.directionDeg! * Math.PI) / 180;
    return {
      dx,
      dy,
      gx: v.slopeDeg * Math.sin(rad),
      gy: -v.slopeDeg * Math.cos(rad),
      w,
    };
  });

  let S00 = 0, S01 = 0, S02 = 0, S11 = 0, S12 = 0, S22 = 0;
  let Tgx0 = 0, Tgx1 = 0, Tgx2 = 0;
  let Tgy0 = 0, Tgy1 = 0, Tgy2 = 0;
  for (const p of pts) {
    S00 += p.w;
    S01 += p.w * p.dx;
    S02 += p.w * p.dy;
    S11 += p.w * p.dx * p.dx;
    S12 += p.w * p.dx * p.dy;
    S22 += p.w * p.dy * p.dy;
    Tgx0 += p.w * p.gx;
    Tgx1 += p.w * p.dx * p.gx;
    Tgx2 += p.w * p.dy * p.gx;
    Tgy0 += p.w * p.gy;
    Tgy1 += p.w * p.dx * p.gy;
    Tgy2 += p.w * p.dy * p.gy;
  }

  const det =
    S00 * (S11 * S22 - S12 * S12) -
    S01 * (S01 * S22 - S12 * S02) +
    S02 * (S01 * S12 - S11 * S02);

  let gx0: number;
  let gy0: number;
  if (!Number.isFinite(det) || Math.abs(det) < 1e-9 || S00 <= 0) {
    gx0 = pts.reduce((s, p) => s + p.gx * p.w, 0) / S00;
    gy0 = pts.reduce((s, p) => s + p.gy * p.w, 0) / S00;
  } else {
    const solveIntercept = (T0: number, T1: number, T2: number) =>
      (T0 * (S11 * S22 - S12 * S12) -
        S01 * (T1 * S22 - S12 * T2) +
        S02 * (T1 * S12 - S11 * T2)) / det;
    gx0 = solveIntercept(Tgx0, Tgx1, Tgx2);
    gy0 = solveIntercept(Tgy0, Tgy1, Tgy2);
  }

  const slopeDeg = Math.sqrt(gx0 * gx0 + gy0 * gy0);
  const directionDeg =
    slopeDeg > 1e-6 ? ((Math.atan2(gx0, -gy0) * 180) / Math.PI + 360) % 360 : null;

  return { slopeDeg, directionDeg };
}

/**
 * Sample the vector field at N evenly-spaced points along the ball→pin
 * line, each with its own local slope magnitude and direction. Feeds
 * `computePuttBreakFromPath` — the path-based read that replaced treating
 * the green as having a single fall line.
 */
export function sampleVectorPath(params: {
  vectors: GreenVector[];
  ballXNorm: number;
  ballYNorm: number;
  pinXNorm: number;
  pinYNorm: number;
  numSamples?: number;
  topN?: number;
}): PuttPathSample[] {
  const {
    vectors,
    ballXNorm,
    ballYNorm,
    pinXNorm,
    pinYNorm,
    numSamples = 6,
    topN = 8,
  } = params;

  if (!vectors.length) return [];

  const samples: PuttPathSample[] = [];
  for (let i = 0; i < numSamples; i++) {
    const t = numSamples === 1 ? 0.5 : i / (numSamples - 1);
    const x = ballXNorm + (pinXNorm - ballXNorm) * t;
    const y = ballYNorm + (pinYNorm - ballYNorm) * t;
    const field = weightedVectorFieldAt(vectors, x, y, topN);
    samples.push({ t, x, y, slopeDeg: field.slopeDeg, directionDeg: field.directionDeg });
  }
  return samples;
}

/**
 * Given normalized ball and pin positions (0–1), sample the vector field
 * along the putt line and return a slope profile for the putt read engine.
 *
 * thetaStart = slope near the ball (first third of putt)
 * thetaMid   = slope at midpoint
 * thetaApex  = peak slope found anywhere along the putt line
 *
 * @deprecated superseded by `sampleVectorPath` + `computePuttBreakFromPath`,
 * which keep each sample's own direction instead of blending everything
 * into 3 magnitude-only numbers. Kept for now only as a reference/fallback.
 */
export function sampleVectorsForPutt(params: {
  vectors: GreenVector[];
  ballXNorm: number;
  ballYNorm: number;
  pinXNorm: number;
  pinYNorm: number;
  topN?: number;
}): PuttSlopeProfile {
  const { vectors, ballXNorm, ballYNorm, pinXNorm, pinYNorm, topN = 5 } =
    params;

  if (!vectors.length) {
    return {
      thetaStartDeg: 0,
      thetaMidDeg: 0,
      thetaApexDeg: 0,
      dominantDirectionDeg: null,
      vectorsUsed: 0,
    };
  }

  // Sample points along the putt line
  const startX = ballXNorm;
  const startY = ballYNorm;
  const midX = (ballXNorm + pinXNorm) / 2;
  const midY = (ballYNorm + pinYNorm) / 2;

  const thetaStart = weightedAvgSlopeDeg(vectors, startX, startY, topN);
  const thetaMid = weightedAvgSlopeDeg(vectors, midX, midY, topN);

  // thetaApex: peak slope of any vector within 0.2 normalized units of the putt line
  const puttLen = Math.sqrt(
    (pinXNorm - ballXNorm) ** 2 + (pinYNorm - ballYNorm) ** 2,
  );

  let thetaApex = Math.max(thetaStart, thetaMid);

  if (puttLen > 0.001) {
    const dx = (pinXNorm - ballXNorm) / puttLen;
    const dy = (pinYNorm - ballYNorm) / puttLen;

    for (const v of vectors) {
      // Perpendicular distance from vector to putt line
      const tx = v.x - ballXNorm;
      const ty = v.y - ballYNorm;
      const proj = tx * dx + ty * dy;
      if (proj < 0 || proj > puttLen) continue;

      const perpDist = Math.abs(tx * dy - ty * dx);
      if (perpDist <= 0.2) {
        thetaApex = Math.max(thetaApex, v.slopeDeg);
      }
    }
  }

  const domDir = dominantDirection(vectors, midX, midY, topN);

  return {
    thetaStartDeg: parseFloat(thetaStart.toFixed(2)),
    thetaMidDeg: parseFloat(thetaMid.toFixed(2)),
    thetaApexDeg: parseFloat(thetaApex.toFixed(2)),
    dominantDirectionDeg: domDir != null ? parseFloat(domDir.toFixed(1)) : null,
    vectorsUsed: vectors.length,
  };
}
